import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useRuleActions } from 'pricing/hooks';
import { IPricingProfileRule } from 'pricing/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  pricingProfileRules: IPricingProfileRule[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemovePricingProfileRulesButton = ({
  pricingProfileRules,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removeRules } = useRuleActions({
    selectionCount: pricingProfileRules.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedRulesIds = pricingProfileRules.map((rule) => rule.id);

    const { success } = await removeRules({ ids: removedRulesIds });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [pricingProfileRules, removeRules, onRemoveSuccess]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePricingProfileRules', { count: pricingProfileRules.length })}
        message={t('messageConfirmRemovePricingProfileRules', {
          count: pricingProfileRules.length,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
